// Rich confirmation modal for form submissions — a styled replacement for the
// native confirm() dialog, used on high-signal actions (e.g. spending AI
// credits). A form opts in by carrying `data-confirm-modal` plus optional
// title / body / confirm-label / cancel-label attributes:
//
//   <form data-confirm-modal
//         data-confirm-modal-title="Ready to submit?"
//         data-confirm-modal-body="This spends 20 AI credits."
//         data-confirm-modal-confirm="Submit (20 credits)"
//         data-confirm-modal-cancel="Not yet">
//
// On confirm we flag the form and re-submit it, letting the real POST through.
import { registerSingleton } from "./first"

const CONFIRMED = "confirmModalConfirmed"

export const ConfirmModal = {
  initialize(): void {
    document.addEventListener("submit", (event) => {
      const form = event.target
      if (!(form instanceof HTMLFormElement)) return
      if (!("confirmModal" in form.dataset)) return
      if (form.dataset[CONFIRMED] === "true") return // already confirmed — let it through

      event.preventDefault()
      openConfirmModal(form)
    })
  },
}

registerSingleton("ConfirmModal", ConfirmModal)

function openConfirmModal(form: HTMLFormElement): void {
  const data = form.dataset
  const overlay = document.createElement("div")
  overlay.className = "confirm-modal"
  overlay.setAttribute("role", "dialog")
  overlay.setAttribute("aria-modal", "true")

  const panel = document.createElement("div")
  panel.className = "confirm-modal__panel"

  const heading = document.createElement("h2")
  heading.className = "confirm-modal__title"
  heading.textContent = data.confirmModalTitle || "Are you sure?"
  panel.appendChild(heading)

  const bodyText = data.confirmModalBody
  if (bodyText) {
    const body = document.createElement("p")
    body.className = "confirm-modal__body"
    body.textContent = bodyText
    panel.appendChild(body)
  }

  const actions = document.createElement("div")
  actions.className = "confirm-modal__actions"

  const cancel = document.createElement("button")
  cancel.type = "button"
  cancel.className = "btn btn--ghost"
  cancel.textContent = data.confirmModalCancel || "Cancel"

  const accept = document.createElement("button")
  accept.type = "button"
  accept.className = "btn btn--primary"
  accept.textContent = data.confirmModalConfirm || "Confirm"

  actions.append(cancel, accept)
  panel.appendChild(actions)
  overlay.appendChild(panel)
  document.body.appendChild(overlay)

  requestAnimationFrame(() => overlay.classList.add("is-open"))
  const previousOverflow = document.body.style.overflow
  document.body.style.overflow = "hidden"
  accept.focus()

  const close = (): void => {
    overlay.remove()
    document.body.style.overflow = previousOverflow
    document.removeEventListener("keydown", onKeydown)
  }

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") close()
  }

  cancel.addEventListener("click", close)
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) close() // click on the backdrop
  })
  accept.addEventListener("click", () => {
    form.dataset[CONFIRMED] = "true"
    close()
    form.requestSubmit()
  })
  document.addEventListener("keydown", onKeydown)
}
